import { useTranslation } from "react-i18next"

import type { AuditEntry } from "@/api/admin-api"

import { ChevronDown, User } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { TableCell, TableRow } from "@/components/ui/table"
import {
  formatActionLabel,
  formatTimestamp,
  getActionColor,
  getValueColor,
} from "@/features/admin-dashboard/components/audit-trail.utils"
import { cn } from "@/lib/utils"

export function ChangeDetails({ changes }: { changes: AuditEntry["changes"] }) {
  const { t } = useTranslation()

  if (Object.keys(changes).length === 0) {
    return <span className="text-muted-foreground text-xs">—</span>
  }

  // Handle batch updates
  if (changes.updates && typeof changes.updates === "object") {
    const updateCount = Object.keys(changes.updates).length
    return (
      <div className="space-y-1 text-xs">
        <div className="text-muted-foreground">
          {t("adminDashboard.auditTrail.changeDetails.batchUpdates")}: {updateCount}
        </div>
        <div className="max-h-24 overflow-y-auto">
          {Object.entries(changes.updates).map(([key, value]) => (
            <div key={key} className="flex items-center gap-1">
              <span className="font-mono text-xs">{key}</span>
              <span className="text-muted-foreground">→</span>
              <Badge className="px-1 py-0 text-xs" variant={value ? "default" : "secondary"}>
                {value
                  ? t("adminDashboard.auditTrail.changeDetails.enabled")
                  : t("adminDashboard.auditTrail.changeDetails.disabled")}
              </Badge>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Handle field-level changes
  if (changes.field) {
    const before = changes.before
    const after = changes.after

    const formatValue = (value: unknown): string => {
      if (typeof value === "boolean") {
        return value
          ? t("adminDashboard.auditTrail.changeDetails.enabled")
          : t("adminDashboard.auditTrail.changeDetails.disabled")
      }
      return String(value ?? "—")
    }

    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-medium">{changes.field}</span>
        <span className="text-muted-foreground">—</span>
        {before !== undefined && (
          <span>
            <span className="text-muted-foreground">
              {t("adminDashboard.auditTrail.changeDetails.before")}:
            </span>{" "}
            <span className={getValueColor(before)}>{formatValue(before)}</span>
          </span>
        )}
        <span>
          <span className="text-muted-foreground">
            {t("adminDashboard.auditTrail.changeDetails.after")}:
          </span>{" "}
          <span className={getValueColor(after)}>{formatValue(after)}</span>
        </span>
      </div>
    )
  }

  // Handle multi-field changes (e.g., config updates with before/after per field)
  if (changes.fields && typeof changes.fields === "object") {
    const formatValue = (value: unknown): string => {
      if (value === null || value === undefined) return "—"
      if (typeof value === "boolean") {
        return value
          ? t("adminDashboard.auditTrail.changeDetails.enabled")
          : t("adminDashboard.auditTrail.changeDetails.disabled")
      }
      if (Array.isArray(value)) {
        return value.length === 0 ? "(empty)" : value.join(", ")
      }
      if (typeof value === "object") {
        return JSON.stringify(value)
      }
      return String(value)
    }

    return (
      <div className="space-y-2 text-sm">
        {Object.entries(changes.fields as Record<string, { before: unknown; after: unknown }>).map(
          ([fieldName, { before, after }]) => (
            <div key={fieldName} className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-medium">{fieldName}</span>
              <span className="text-muted-foreground">—</span>
              {before !== undefined && (
                <span>
                  <span className="text-muted-foreground">
                    {t("adminDashboard.auditTrail.changeDetails.before")}:
                  </span>{" "}
                  <span className={getValueColor(before)}>{formatValue(before)}</span>
                </span>
              )}
              <span>
                <span className="text-muted-foreground">
                  {t("adminDashboard.auditTrail.changeDetails.after")}:
                </span>{" "}
                <span className={getValueColor(after)}>{formatValue(after)}</span>
              </span>
            </div>
          )
        )}
      </div>
    )
  }

  // Handle updated_fields (legacy format - just field names)
  if (changes.updated_fields && Array.isArray(changes.updated_fields)) {
    return (
      <div className="text-sm">
        <span className="text-muted-foreground">Updated: </span>
        {changes.updated_fields.join(", ")}
      </div>
    )
  }

  // Handle access policy changes (before/after objects with requires/block)
  if (changes.before !== undefined || changes.after !== undefined) {
    const before = changes.before as Record<string, unknown> | null | undefined
    const after = changes.after as Record<string, unknown> | null | undefined

    const formatPolicy = (policy: Record<string, unknown> | null | undefined): React.ReactNode => {
      if (!policy || Object.keys(policy).length === 0) {
        return <span className="text-muted-foreground italic">No restrictions</span>
      }

      const parts: React.ReactNode[] = []
      const requires = policy.requires as Record<string, string[]> | undefined
      const block = policy.block as Record<string, string[]> | undefined

      if (requires) {
        if (requires.oids.length) {
          parts.push(
            <span key="req-oids" className="text-success">
              Requires: {requires.oids.length} user(s)
            </span>
          )
        }
        if (requires.groups.length) {
          parts.push(
            <span key="req-groups" className="text-success">
              Requires groups: {requires.groups.join(", ")}
            </span>
          )
        }
      }

      if (block) {
        if (block.oids.length) {
          parts.push(
            <span key="block-oids" className="text-destructive">
              Blocked: {block.oids.length} user(s)
            </span>
          )
        }
        if (block.groups.length) {
          parts.push(
            <span key="block-groups" className="text-destructive">
              Blocked groups: {block.groups.join(", ")}
            </span>
          )
        }
      }

      return parts.length > 0 ? (
        <span className="flex flex-wrap gap-2">{parts}</span>
      ) : (
        <span className="text-muted-foreground italic">No restrictions</span>
      )
    }

    return (
      <div className="space-y-2 text-sm">
        <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
          <span className="font-medium">Access Policy</span>
          <span className="text-muted-foreground">—</span>
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 pl-4">
          <span className="text-muted-foreground">
            {t("adminDashboard.auditTrail.changeDetails.before")}:
          </span>
          <div>{formatPolicy(before)}</div>
          <span className="text-muted-foreground">
            {t("adminDashboard.auditTrail.changeDetails.after")}:
          </span>
          <div>{formatPolicy(after)}</div>
        </div>
      </div>
    )
  }

  return <span className="text-muted-foreground text-xs">—</span>
}

export function AuditTableRow({
  entry,
  isExpanded,
  onToggle,
}: {
  entry: AuditEntry
  isExpanded: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const hasDetails = Object.keys(entry.changes).length > 0

  return (
    <>
      <TableRow
        className={cn(
          "hover:bg-muted/50 cursor-pointer transition-colors",
          isExpanded && "bg-muted/30"
        )}
        onClick={onToggle}
      >
        <TableCell className="text-muted-foreground px-4 py-3 font-mono text-sm whitespace-nowrap">
          {formatTimestamp(entry.timestamp)}
        </TableCell>
        <TableCell className="px-4 py-3 text-sm">
          <div className="flex items-center gap-2">
            <User className="text-muted-foreground size-3.5 shrink-0" />
            <span className="max-w-[180px] truncate" title={entry.user_email ?? entry.user_id}>
              {entry.user_email ?? entry.user_id}
            </span>
          </div>
        </TableCell>
        <TableCell className="px-4 py-3">
          <Badge className={cn("text-xs font-medium", getActionColor(entry.action))}>
            {formatActionLabel(entry.action)}
          </Badge>
        </TableCell>
        <TableCell className="px-4 py-3 text-sm">
          <div className="flex items-center gap-2">
            <Badge className="shrink-0 text-xs capitalize" variant="outline">
              {t(
                `adminDashboard.auditTrail.resourceTypes.${entry.resource_type}`,
                entry.resource_type
              )}
            </Badge>
            <span
              className="text-muted-foreground max-w-[140px] truncate font-mono text-xs"
              title={entry.resource_id}
            >
              {entry.resource_id}
            </span>
          </div>
        </TableCell>
        <TableCell className="px-2 py-3 text-right">
          {hasDetails && (
            <ChevronDown
              className={cn(
                "text-muted-foreground size-4 transition-transform",
                isExpanded && "rotate-180"
              )}
            />
          )}
        </TableCell>
      </TableRow>
      {isExpanded && hasDetails && (
        <TableRow className="bg-muted/20 border-t-0">
          <TableCell className="px-6 py-4 whitespace-normal" colSpan={5}>
            <ChangeDetails changes={entry.changes} />
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export function AuditTableSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
  )
}
